// react
import { useMemo, useState } from 'react';

// mui
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

// components
import { BottomSheetDialog } from '@/pwa/atoms/BottomSheetDialog/BottomSheetDialog';
import { Icon } from '@/components/atoms/Icon/Icon';

// models
import { ShortlistItem, SHORTLIST_ITEM_STATUS } from '@karehero/models';

// helpers
import { formatAddress } from '@karehero/helpers/address';

export interface ProviderDrawerProps {
  item: ShortlistItem | null;
  onClose: () => void;
  onMarkSecured: (item: ShortlistItem) => void;
  onBookVisit: () => void;
  onCopy: (value: string, label: string) => void;
}

const InfoRow = ({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy?: (value: string, label: string) => void;
}) => (
  <Stack spacing={0.25}>
    <Typography
      variant='caption'
      sx={{
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Stack
      direction='row'
      spacing={0.5}
      sx={{
        alignItems: 'center',
      }}
    >
      <Typography variant='bodyBold'>{value}</Typography>
      {onCopy && (
        <IconButton
          size='small'
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={() => onCopy(value, label)}
        >
          <Icon icon='copy' fontSize={16} />
        </IconButton>
      )}
    </Stack>
  </Stack>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card variant='outlined'>
    <CardContent>
      <Stack spacing={1}>
        <Typography variant='h6'>{title}</Typography>
        {children}
      </Stack>
    </CardContent>
  </Card>
);

const formatRatingLabel = (r: string): string =>
  r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' ');

export const ProviderDrawer = ({
  item,
  onClose,
  onMarkSecured,
  onBookVisit,
  onCopy,
}: ProviderDrawerProps) => {
  const provider = item?.provider;
  const isSecured = item?.status === SHORTLIST_ITEM_STATUS.SECURED;
  const hasContact = !!(provider?.enquiryPhone || provider?.enquiryEmail);

  const address = useMemo(
    () => (provider?.address ? formatAddress(provider.address) : ''),
    [provider],
  );

  const ratingLabel = useMemo(() => {
    const r = provider?.rating?.overallRating;
    if (!r) return null;
    return formatRatingLabel(r);
  }, [provider]);

  const priceLabel = useMemo(() => {
    if (!provider?.pricePerWeek) return null;
    return `£${provider.pricePerWeek.toLocaleString()}/week`;
  }, [provider]);

  const ratings = provider?.rating?.ratings;
  const hasInfoGrid =
    ratingLabel ||
    priceLabel ||
    provider?.bedCount != null ||
    provider?.managerName ||
    hasContact;
  const hasServiceTypes =
    provider?.careServiceTypes && provider.careServiceTypes.length > 0;
  const hasSpecialisms =
    provider?.specialisms && provider.specialisms.length > 0;
  const hasRatingDetail = ratings != null;

  const features = useMemo(() => {
    if (!provider) return [];

    const aggregated: string[] = [];

    if (provider.enSuiteRoomCount)
      aggregated.push(`${provider.enSuiteRoomCount} en-suite rooms`);
    if (provider.doubleBedCount)
      aggregated.push(`${provider.doubleBedCount} double rooms`);
    if (provider.purposeBuilt) aggregated.push('Purpose built');
    if (provider.dateOpened)
      aggregated.push(`Established ${provider.dateOpened.slice(0, 4)}`);

    return aggregated;
  }, [provider]);

  const [photoIndex, setPhotoIndex] = useState(0);

  if (!item || !provider) return null;

  return (
    <BottomSheetDialog
      isOpen
      warm
      onClose={onClose}
      title={<Typography variant='h4'>{provider.name}</Typography>}
      footer={
        <Stack
          spacing={1}
          sx={{
            width: '100%',
          }}
        >
          <Button
            variant='contained'
            fullWidth
            size='large'
            onClick={() => onMarkSecured(item)}
          >
            {isSecured ? 'Mark as not secured' : 'Mark as secured'}
          </Button>
          {hasContact && (
            <Button
              variant='outlined'
              fullWidth
              size='large'
              onClick={onBookVisit}
            >
              Book a visit
            </Button>
          )}
        </Stack>
      }
    >
      <Stack spacing={2}>
        {provider.photos?.length > 0 && (
          <>
            <CardMedia
              component='img'
              image={provider.photos[photoIndex]?.large}
              alt={provider.name}
              sx={{ borderRadius: 1, maxHeight: 240, objectFit: 'cover' }}
            />
            {provider.photos.length > 1 && (
              <Stack direction='row' spacing={1}>
                {provider.photos.slice(0, 4).map((photo, i) => (
                  <Box
                    key={i}
                    component='img'
                    src={photo.small}
                    alt=''
                    onClick={() => setPhotoIndex(i)}
                    sx={{
                      width: 80,
                      height: 60,
                      borderRadius: 0.5,
                      borderColor: 'primary.main',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      opacity: i === photoIndex ? 1 : 0.6,
                      border: i === photoIndex ? 2 : 0,
                    }}
                  />
                ))}
              </Stack>
            )}
          </>
        )}

        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
          }}
        >
          {address}
        </Typography>

        {isSecured && <Chip label='Secured' color='success' size='small' />}

        {item.note && (
          <>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
              }}
            >
              Note from your care expert
            </Typography>
            <Typography variant='body2'>{item.note}</Typography>
          </>
        )}

        {hasInfoGrid && (
          <Card variant='outlined'>
            <CardContent>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                }}
              >
                {ratingLabel && (
                  <InfoRow label='Regulator rating' value={ratingLabel} />
                )}
                {priceLabel && <InfoRow label='Price' value={priceLabel} />}
                {provider.bedCount != null && (
                  <InfoRow label='Beds' value={String(provider.bedCount)} />
                )}
                {provider.managerName && (
                  <InfoRow label='Manager' value={provider.managerName} />
                )}
              </Box>
              {hasContact && (
                <Stack
                  spacing={1}
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  {provider.enquiryPhone && (
                    <InfoRow
                      label='Phone'
                      value={provider.enquiryPhone}
                      onCopy={onCopy}
                    />
                  )}
                  {provider.enquiryEmail && (
                    <InfoRow
                      label='Email'
                      value={provider.enquiryEmail}
                      onCopy={onCopy}
                    />
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {provider.description && (
          <Section title='About'>
            <Stack spacing={1}>
              {provider.description.split(/\r?\n\r?\n/).map((para, i) => (
                <Typography key={i} variant='body2'>
                  {para}
                </Typography>
              ))}
            </Stack>
          </Section>
        )}

        {hasServiceTypes && (
          <Section title='Types of care'>
            <Stack
              direction='row'
              spacing={1}
              useFlexGap
              sx={{
                flexWrap: 'wrap',
              }}
            >
              {provider.careServiceTypes.map((st) => (
                <Chip key={st.id} label={st.name} size='small' />
              ))}
            </Stack>
          </Section>
        )}

        {hasSpecialisms && (
          <Section title='Services and specialisms'>
            <Stack
              direction='row'
              spacing={1}
              useFlexGap
              sx={{
                flexWrap: 'wrap',
              }}
            >
              {provider.specialisms!.map((s) => (
                <Chip key={s} label={s} size='small' variant='outlined' />
              ))}
            </Stack>
          </Section>
        )}

        {features.length > 0 && (
          <Section title='Features'>
            <Stack
              spacing={0.5}
              component='ul'
              sx={{
                m: 0,
                pl: 2.5,
              }}
            >
              {features.map((f) => (
                <Typography key={f} variant='body2' component='li'>
                  {f}
                </Typography>
              ))}
            </Stack>
          </Section>
        )}

        {provider.fundings && provider.fundings.length > 0 && (
          <Section title='Pricing and funding'>
            <Typography variant='body2'>
              {provider.fundings.map((f) => f.name).join(', ')}
            </Typography>
          </Section>
        )}

        {hasRatingDetail && (
          <Section title='Regulator rating'>
            {ratingLabel && (
              <Typography variant='body2'>Overall: {ratingLabel}</Typography>
            )}
            <Stack spacing={0.5}>
              {ratings!.safe && (
                <InfoRow
                  label='Safe'
                  value={formatRatingLabel(ratings!.safe)}
                />
              )}
              {ratings!.effective && (
                <InfoRow
                  label='Effective'
                  value={formatRatingLabel(ratings!.effective)}
                />
              )}
              {ratings!.caring && (
                <InfoRow
                  label='Caring'
                  value={formatRatingLabel(ratings!.caring)}
                />
              )}
              {ratings!.responsive && (
                <InfoRow
                  label='Responsive'
                  value={formatRatingLabel(ratings!.responsive)}
                />
              )}
              {ratings!.wellLed && (
                <InfoRow
                  label='Well led'
                  value={formatRatingLabel(ratings!.wellLed)}
                />
              )}
            </Stack>
          </Section>
        )}
      </Stack>
    </BottomSheetDialog>
  );
};

ProviderDrawer.displayName = 'ProviderDrawer';

export default ProviderDrawer;
